import { TResult, makeSuccess, ISgApiService, IEndpoint } from "../../../sg-core";
import IFormType from "../interfaces/IFormType";
import ICategory from "../interfaces/ICategory";
import IFormDefinition from "../interfaces/IFormDefinition";
import FormDefinitionMapper from "../mappers/FormDefinitionMapper";
import IPerson from "../interfaces/IPerson";
import FormTypeMapper from "../mappers/FormTypeMapper";
import CategoryMapper from "../mappers/CategoryMapper";
import PermissionMapper from "../mappers/PermissionMapper";
import { SemanticICONS } from "semantic-ui-react/dist/commonjs/generic";
import { IPermissionDTO, IAppRoleDTO, ICategoryPermission } from "../interfaces/ICategoryPermission";
import { IRole } from "../interfaces/IHavainnotUser";
import { RoleMapper } from "../mappers/RoleMapper";
import { AppParameterMapper, IAppParatemers, IAppParatemersDTO } from "../interfaces/AppParatemers";
import {
    AppConfigurationMapper,
    IAppConfigurations,
    IAppConfigurationsDTO,
} from "../interfaces/IAppConfigurations";
import { IFormTypeDTO } from "@/@dokumentit/tyoluvat/models";
import FormDefinitionMapperCorrected from "../mappers/FormDefinitionMapperCorrected";

export interface IInitialContextData {
    formTypes: IFormType[];
    categores: ICategory[];
    persons: IPerson[];
    categoryPermissions: ICategoryPermission[];
    appRoles: IRole[];
    appParameters: IAppParatemers | null;
    appConfigs: IAppConfigurations | null;
}

export interface IContextService {
    getInitialData(): Promise<TResult<IInitialContextData>>;
    getFormDefinition(search: {
        formId?: string | number;
        formDefinitionGuid?: string;
    }): Promise<TResult<IFormDefinition>>;
    getFormDefinitionCorrected(formDefinitionId: string | number): Promise<TResult<IFormDefinition>>;
    getUserAppRoles: () => Promise<TResult<any>>;
    getAnonymousForm: (guid: string) => Promise<TResult<{ formTypeId: number; token: string }>>;
    getFormType: (id: IFormType["id"]) => Promise<TResult<IFormType>>;
}

export interface IInitialContextResult {
    success: true;
    // action: "hae_perustiedot";
    henkilot: { id: number; nimi: string; kuva: string; etunimi: string; sukunimi: string }[];
    lomakkeet: {
        id: number;
        guid: string;
        nimi: string;
        kategoria: number;
        lomakemaaritys: number;
        tyonkulku: number;
    }[];
    kategoriat: {
        id: number;
        nimi: string;
        ikoni?: SemanticICONS;
        kasittelylomakemaaritys?: IFormDefinition["id"];
    }[];
    oikeustasot?: IPermissionDTO[];
    roolit?: IAppRoleDTO[];
    parametrit?: IAppParatemersDTO;
    config: IAppConfigurationsDTO;
}

export interface IFormConfigResult {
    success: true;
    action: "hae_perustiedot";
    lomakemaaritys: { id: number; nimi: string; kategoria: number; tyonkulku: number; data: string };
    // lomake: { id: number; nimi: string; kategoria: number; tyonkulku: number; lomakemaaritys: { data: string } }
}

export interface IFormDefinitionResult {
    success: true;
    action: "hae_lomakemaaritys_korjattu";
    lomakemaaritys: { id: number; nimi: string; data: string };
}

export const getContextService = (
    apiService: ISgApiService,
    Enpoint: IEndpoint,
    anonymousSession____: boolean = false,
): IContextService => {
    let anonymousSession = window.location.pathname.indexOf("anonyymi") > -1;

    const getInitialData = async (): Promise<TResult<IInitialContextData>> => {
        const action = anonymousSession ? "hae_perustiedot_anonyymi" : "hae_perustiedot";
        const apiResponse = await apiService.sendAction<IInitialContextResult>(Enpoint, action, {});
        if (apiResponse.isSuccess === true) {
            return makeSuccess<IInitialContextData>({
                formTypes: apiResponse.value.lomakkeet.map(FormTypeMapper.apiToDomain),
                categores: apiResponse.value.kategoriat.map(CategoryMapper.apiToDomain),
                // locations: apiResponse.value.sijainnit.map(LocationMapper.apiToDomain),
                // facilities: apiResponse.value.kohteet.map(FacilityMapper.apiToDomain),
                // areas: apiResponse.value.alueet.map(AreaMapper.apiToDomain),
                persons: [],
                categoryPermissions: PermissionMapper.permissionsToDomain(
                    apiResponse.value.oikeustasot || [],
                ),
                appRoles: apiResponse.value.roolit
                    ? apiResponse.value.roolit.map(RoleMapper.apiToDomain)
                    : [],
                appParameters: apiResponse.value.parametrit
                    ? AppParameterMapper.apiToDomain(apiResponse.value.parametrit)
                    : null,
                appConfigs: apiResponse.value.config
                    ? AppConfigurationMapper.apiToDomain(apiResponse.value.config)
                    : null,
            });
        }
        return apiResponse;
    };

    const getUserAppRoles = async (): Promise<TResult<any>> => {
        const action = "hae_kayttajaroolit";
        const body = {};
        const apiResponse = await apiService.sendAction<any>(Enpoint, action, body);
        if (apiResponse.isSuccess === true) {
            return makeSuccess(apiResponse.value.roolilista.split(","));
        }
        return apiResponse;
    };

    const getAnonymousForm = async (
        guid: string,
    ): Promise<TResult<{ formTypeId: number; token: string }>> => {
        const action = "kasittele_lomakeavaus_anonyymi";
        const body = { lomake: { guid } };
        const apiResponse = await apiService.sendAction<any>(Enpoint, action, body, true);
        if (apiResponse.isSuccess === true) {
            return makeSuccess({
                formTypeId: apiResponse.value.lomake.id,
                token: apiResponse.value.sid,
            });
        }
        return apiResponse;
    };

    const getFormDefinition: IContextService["getFormDefinition"] = async ({
        formId,
        formDefinitionGuid,
    }): Promise<TResult<IFormDefinition>> => {
        const action = anonymousSession ? "hae_lomakemaaritys_anonyymi" : "hae_lomakemaaritys";
        const body = { lomakemaaritys: { id: formId, guid: formDefinitionGuid } };
        const apiResponse = await apiService.sendAction<IFormConfigResult>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess(FormDefinitionMapper.apiToDomain(apiResponse.value.lomakemaaritys));
        }
        return apiResponse;
    };

    const getFormType: IContextService["getFormType"] = async (id): Promise<TResult<IFormType>> => {
        const action = "hae_lomake";
        const body = { lomake: { id } };
        const apiResponse = await apiService.sendAction<{ lomake: IFormTypeDTO }>(Enpoint, action, body);

        if (apiResponse.isSuccess === true) {
            return makeSuccess(FormTypeMapper.apiToDomain(apiResponse.value.lomake));
        }
        return apiResponse;
    };

    const getFormDefinitionCorrected = async (
        formDefinitionId: string | number,
    ): Promise<TResult<IFormDefinition>> => {
        const action = "hae_lomakemaaritys_korjattu";
        const body = { lomakemaaritys: { id: formDefinitionId } };
        const apiResponse = await apiService.sendAction<IFormDefinitionResult>(Enpoint, action, body);
        if (apiResponse.isSuccess === true) {
            return makeSuccess(FormDefinitionMapperCorrected.apiToDomain(apiResponse.value.lomakemaaritys));
        }
        return apiResponse;
    };

    return {
        getInitialData,
        getFormDefinition,
        getUserAppRoles,
        getAnonymousForm,
        getFormType,
        getFormDefinitionCorrected,
    };
};
